/**
 * CRM field catalog (contacts and leads). Same keys, types and options as the API
 * (FillSign.Infrastructure/Crm/CrmFieldCatalog.cs); labels live here.
 */

export type CrmEntity = "contact" | "lead";
export type Lang = "pt" | "en";

export type FieldType =
  | "text" | "email" | "phone" | "url" | "number" | "decimal" | "option"
  | "textarea" | "postalCode" | "boolean" | "user" | "lookup" | "datetime" | "cpf" | "cnpj";

export interface FieldDef {
  key: string;
  type: FieldType;
  pt: string;
  en: string;
  maxLength?: number;
  options?: string[];
  /** Can be placed on the form by the form designer. */
  placeable: boolean;
  /** System field: shown, never edited on the form. */
  readOnly?: boolean;
}

const f = (key: string, type: FieldType, pt: string, en: string, extra: Partial<FieldDef> = {}): FieldDef => ({
  key, type, pt, en, placeable: true, maxLength: 200, ...extra,
});
const sys = (key: string, type: FieldType, pt: string, en: string): FieldDef => ({
  key, type, pt, en, placeable: false, readOnly: true,
});

export const RELATIONSHIP_TYPES = ["customer", "prospect", "partner", "supplier", "employee", "other"];
export const EMAIL_KINDS = ["personal", "corporate"] as const;
export type EmailKind = (typeof EMAIL_KINDS)[number];
export const LEAD_RATINGS = ["hot", "warm", "cold"];
export const LEAD_STATUSES = ["new", "contacted", "qualified", "not_qualified", "accepted"];
export const LEAD_STAGES = ["new", "qualify", "assign", "accept"] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];
export const INDUSTRIES = [
  "agribusiness", "construction", "consulting", "education", "energy", "finance", "food", "government",
  "healthcare", "hospitality", "insurance", "legal", "logistics", "manufacturing", "media", "real_estate",
  "retail", "services", "technology", "telecom", "other",
];

const CONTACT_FIELDS: FieldDef[] = [
  f("firstName", "text", "Nome", "First name", { maxLength: 100 }),
  f("lastName", "text", "Sobrenome", "Last name", { maxLength: 100 }),
  sys("fullName", "text", "Nome completo", "Full name"),
  f("legalName", "text", "Nome de registro", "Legal name"),
  f("jobTitle", "text", "Cargo", "Job title", { maxLength: 100 }),
  f("personalEmail", "email", "E-mail pessoal", "Personal email", { maxLength: 254 }),
  f("corporateEmail", "email", "E-mail corporativo", "Work email", { maxLength: 254 }),
  // Set with the star next to the two e-mails; "email" is the default one (views, search, sending).
  { ...sys("preferredEmail", "option", "E-mail padrão", "Default email"), readOnly: false, options: [...EMAIL_KINDS] },
  sys("email", "email", "E-mail padrão", "Default email"),
  f("cpf", "cpf", "CPF", "CPF (Brazilian ID)", { maxLength: 20 }),
  f("cnpj", "cnpj", "CNPJ", "CNPJ (company ID)", { maxLength: 24 }),
  f("relationshipType", "option", "Tipo de relacionamento", "Relationship type", { options: RELATIONSHIP_TYPES }),
  f("department", "text", "Departamento", "Department", { maxLength: 100 }),
  f("companyName", "text", "Empresa", "Company name"),
  f("tags", "text", "Etiquetas", "Tags", { maxLength: 500 }),
  f("mobilePhone", "phone", "Celular", "Mobile phone", { maxLength: 30 }),
  f("businessPhone", "phone", "Telefone comercial", "Business phone", { maxLength: 30 }),
  f("linkedIn", "url", "LinkedIn", "LinkedIn", { maxLength: 300 }),
  f("street1", "text", "Logradouro", "Street"),
  f("street2", "text", "Complemento", "Street 2"),
  f("city", "text", "Cidade", "City", { maxLength: 100 }),
  f("postalCode", "postalCode", "CEP", "ZIP/Postal code", { maxLength: 20 }),
  f("stateProvince", "text", "Estado/Província", "State/Province", { maxLength: 100 }),
  f("country", "text", "País/Região", "Country/Region", { maxLength: 100 }),
  { ...sys("ownerId", "user", "Proprietário", "Owner"), readOnly: false },
  sys("isActive", "boolean", "Situação", "Record status"),
  sys("createdOn", "datetime", "Criado em", "Created on"),
  sys("createdBy", "user", "Criado por", "Created by"),
  sys("modifiedOn", "datetime", "Modificado em", "Modified on"),
  sys("modifiedBy", "user", "Modificado por", "Modified by"),
  sys("lastDocumentAt", "datetime", "Último documento", "Last document"),
  sys("photoUpdatedAt", "datetime", "Foto", "Photo"),
];

const LEAD_FIELDS: FieldDef[] = [
  f("topic", "text", "Tópico", "Topic", { maxLength: 300 }),
  f("companyName", "text", "Empresa", "Company name"),
  f("rating", "option", "Classificação", "Rating", { options: LEAD_RATINGS }),
  f("status", "option", "Status", "Status", { options: LEAD_STATUSES }),
  f("description", "textarea", "Descrição", "Description", { maxLength: 4000 }),
  f("firstName", "text", "Nome", "First name", { maxLength: 100 }),
  f("lastName", "text", "Sobrenome", "Last name", { maxLength: 100 }),
  sys("fullName", "text", "Nome completo", "Full name"),
  f("jobTitle", "text", "Cargo", "Job title", { maxLength: 100 }),
  f("email", "email", "E-mail", "Email", { maxLength: 254 }),
  f("mobilePhone", "phone", "Celular", "Mobile phone", { maxLength: 30 }),
  f("businessPhone", "phone", "Telefone comercial", "Business phone", { maxLength: 30 }),
  f("website", "url", "Site", "Website", { maxLength: 300 }),
  f("numberOfEmployees", "number", "Nº de funcionários", "No. of employees"),
  f("industry", "option", "Setor", "Industry", { options: INDUSTRIES }),
  f("marketSector", "text", "Segmento de mercado", "Market sector", { maxLength: 100 }),
  f("postalCode", "postalCode", "CEP", "ZIP/Postal code", { maxLength: 20 }),
  f("street1", "text", "Logradouro", "Street"),
  f("addressNumber", "text", "Número", "Number", { maxLength: 20 }),
  f("street2", "text", "Complemento", "Complement"),
  f("neighborhood", "text", "Bairro", "Neighborhood", { maxLength: 100 }),
  f("city", "text", "Cidade", "City", { maxLength: 100 }),
  f("stateProvince", "text", "Estado (UF)", "State", { maxLength: 100 }),
  f("country", "text", "País", "Country", { maxLength: 100 }),
  { ...sys("latitude", "decimal", "Latitude", "Latitude"), readOnly: false },
  { ...sys("longitude", "decimal", "Longitude", "Longitude"), readOnly: false },
  { ...sys("addressValidated", "boolean", "Endereço validado", "Address validated"), readOnly: false },
  f("assignedToUserId", "user", "Atribuído a", "Assigned to"),
  f("assignedToName", "text", "Atribuído a (nome)", "Assigned to (name)"),
  f("contactId", "lookup", "Cliente", "Contact"),
  { ...sys("stage", "option", "Etapa", "Stage"), options: [...LEAD_STAGES] },
  { ...sys("ownerId", "user", "Proprietário", "Owner"), readOnly: false },
  sys("isActive", "boolean", "Situação", "Record status"),
  sys("acceptedAt", "datetime", "Aceito em", "Accepted on"),
  sys("createdOn", "datetime", "Criado em", "Created on"),
  sys("createdBy", "user", "Criado por", "Created by"),
  sys("modifiedOn", "datetime", "Modificado em", "Modified on"),
  sys("modifiedBy", "user", "Modificado por", "Modified by"),
];

const CATALOG: Record<CrmEntity, FieldDef[]> = { contact: CONTACT_FIELDS, lead: LEAD_FIELDS };

export const fieldsOf = (entity: CrmEntity): FieldDef[] => CATALOG[entity];

// Free mailbox providers: an address there is personal, anything else is a work e-mail (same list as the API).
const FREE_MAIL = new Set([
  "gmail.com", "googlemail.com", "hotmail.com", "hotmail.com.br", "outlook.com", "outlook.com.br", "live.com",
  "msn.com", "yahoo.com", "yahoo.com.br", "ymail.com", "icloud.com", "me.com", "mac.com", "aol.com",
  "uol.com.br", "bol.com.br", "terra.com.br", "ig.com.br", "globo.com", "globomail.com", "r7.com",
  "protonmail.com", "proton.me", "zoho.com", "gmx.com", "mail.com", "yandex.com",
]);

export function guessEmailKind(email: string | null | undefined): EmailKind {
  const domain = (email || "").trim().toLowerCase().split("@")[1] || "";
  return !domain || FREE_MAIL.has(domain) ? "personal" : "corporate";
}

/** Person fields of a lead that come from its client (read-only on the lead). */
export const LEAD_MIRRORED = ["firstName", "lastName", "jobTitle", "email", "mobilePhone", "businessPhone"];
export const fieldDef = (entity: CrmEntity, key: string): FieldDef | undefined => CATALOG[entity].find((x) => x.key === key);
export const placeableFields = (entity: CrmEntity): FieldDef[] => CATALOG[entity].filter((x) => x.placeable);

export function fieldLabel(entity: CrmEntity, key: string, lang: Lang): string {
  const def = fieldDef(entity, key);
  if (!def) return key;
  return lang === "pt" ? def.pt : def.en;
}

const OPTION_LABELS: Record<string, Record<string, [string, string]>> = {
  relationshipType: {
    customer: ["Cliente", "Customer"], prospect: ["Prospect", "Prospect"], partner: ["Parceiro", "Partner"],
    supplier: ["Fornecedor", "Supplier"], employee: ["Colaborador", "Employee"], other: ["Outro", "Other"],
  },
  rating: { hot: ["Quente", "Hot"], warm: ["Morno", "Warm"], cold: ["Frio", "Cold"] },
  preferredEmail: { personal: ["Pessoal", "Personal"], corporate: ["Corporativo", "Work"] },
  status: {
    new: ["Novo", "New"], contacted: ["Contatado", "Contacted"], qualified: ["Qualificado", "Qualified"],
    not_qualified: ["Não qualificado", "Not qualified"], accepted: ["Aceito por vendas", "Sales accepted"],
  },
  stage: { new: ["Novo", "New"], qualify: ["Qualificar", "Qualify"], assign: ["Atribuir", "Assign"], accept: ["Aceite de vendas", "Sales accept"] },
  industry: {
    agribusiness: ["Agronegócio", "Agribusiness"], construction: ["Construção", "Construction"], consulting: ["Consultoria", "Consulting"],
    education: ["Educação", "Education"], energy: ["Energia", "Energy"], finance: ["Finanças", "Finance"],
    food: ["Alimentos e bebidas", "Food & beverage"], government: ["Governo", "Government"], healthcare: ["Saúde", "Healthcare"],
    hospitality: ["Hotelaria e turismo", "Hospitality"], insurance: ["Seguros", "Insurance"], legal: ["Jurídico", "Legal"],
    logistics: ["Logística", "Logistics"], manufacturing: ["Indústria", "Manufacturing"], media: ["Mídia e comunicação", "Media"],
    real_estate: ["Imobiliário", "Real estate"], retail: ["Varejo", "Retail"], services: ["Serviços", "Services"],
    technology: ["Tecnologia", "Technology"], telecom: ["Telecomunicações", "Telecom"], other: ["Outro", "Other"],
  },
};

export function optionLabel(key: string, value: string | null | undefined, lang: Lang): string {
  if (!value) return "";
  const pair = OPTION_LABELS[key]?.[value];
  return pair ? (lang === "pt" ? pair[0] : pair[1]) : value;
}

/** "Ativo/Inativo" for contacts, "Aberto/Encerrado" for leads. */
export function stateLabel(entity: CrmEntity, active: boolean, lang: Lang): string {
  if (entity === "lead") return active ? (lang === "pt" ? "Aberto" : "Open") : (lang === "pt" ? "Encerrado" : "Closed");
  return active ? (lang === "pt" ? "Ativo" : "Active") : (lang === "pt" ? "Inativo" : "Inactive");
}

const SECTION_LABELS: Record<string, [string, string]> = {
  general: ["Informações gerais", "General information"],
  contactInfo: ["Informações de contato", "Contact information"],
  system: ["Informações do sistema", "System information"],
  leadInfo: ["Informações do lead", "Lead information"],
  companyInfo: ["Informações da empresa", "Company information"],
  address: ["Endereço", "Address"],
};

export function sectionLabel(id: string, custom: string | null | undefined, lang: Lang, entity?: CrmEntity): string {
  if (custom && custom.trim()) return custom;
  // On a lead the person block is the client the lead belongs to.
  const pair = entity === "lead" && id === "contactInfo" ? (["Cliente", "Contact"] as [string, string]) : SECTION_LABELS[id];
  return pair ? (lang === "pt" ? pair[0] : pair[1]) : (lang === "pt" ? "Seção" : "Section");
}

export function entityLabel(entity: CrmEntity, lang: Lang, plural = false): string {
  if (entity === "lead") return plural ? "Leads" : "Lead";
  if (lang === "pt") return plural ? "Clientes" : "Cliente";
  return plural ? "Contacts" : "Contact";
}

/** Fields shown in each lead process stage (BPF), in order. "assignedTo" is the owner pick (user or free text). */
export const STAGE_FIELDS: Record<LeadStage, string[]> = {
  new: ["contactId", "companyName", "status", "rating"],
  qualify: ["status"],
  assign: ["status", "assignedTo"],
  accept: [],
};
